/**
 * This file tracks events specific to the QuickStart component.
 */
import {FrameworkName} from '../../components/QuickStart/types';
import {makeTrackEvent} from './makeTrackEvent';

export enum QuickStartAnalyticsOrigin {
  QUICKSTART_PAGE = 'quickstart page',
  PROJECT_PAGE = 'project page',
  TEAM_PAGE = 'team page',
}

export const enum QuickStartEventNames {
  QuickStartViewed = 'Quickstart Viewed',
  ClickedOpenInColab = 'Clicked Open in Colab',
  CopiedApiKey = 'Copied api key',
  CopiedCodeBlock = 'Copied code block',
  ClickedWeavePlayground = 'Clicked Weave Playground',
  AuthorizedFromColab = 'Authorized from Colab',
}

type QuickStartDefaultData = {
  projectName?: string;
  entityName: string;
  location: QuickStartAnalyticsOrigin;
};

type QuickStartDefaultDescriptions = {
  entityName: {
    description: 'The entity where the quick start content is being displayed.';
    exampleValues: ['my-org', 'my-user'];
  };
  projectName: {
    description: 'The project being viewed.';
    exampleValues: ['my-project', undefined];
  };
  location: {
    description: '';
    exampleValues: [
      QuickStartAnalyticsOrigin.QUICKSTART_PAGE,
      QuickStartAnalyticsOrigin.PROJECT_PAGE,
      QuickStartAnalyticsOrigin.TEAM_PAGE,
    ];
  };
};

export const quickStartViewed = makeTrackEvent<
  {quickstartType?: string; quickstartVariant?: string} & QuickStartDefaultData,
  {
    _description: 'A user viewed the quick start content.';
    _location: 'Quick start content can appear on QuickStart page, Project page, Team page.';
    _motivation: 'Track data to understand how users interact with quick start content.';
    quickstartType: {
      description: 'Which quickstart was viewed';
      exampleValues: ['models', 'weave'];
    };
    quickstartVariant: {
      description: 'The variant of the quickstart that was viewed';
      exampleValues: ['weave-code', 'weave-playground'];
    };
  } & QuickStartDefaultDescriptions
>(QuickStartEventNames.QuickStartViewed);

export const clickedOpenInColab = makeTrackEvent<
  {
    frameworkName: string;
    colabLink: string;
  } & QuickStartDefaultData,
  {
    _description: "Clicking on the 'Open in Colab' button in quick start will open the selected framework's quick start in Colab.";
    _location: "'Open in Colab' button in quick start content.";
    _motivation: "Track data to determine how frequently users open a framework's quick start in Colab.";
    frameworkName: {
      description: 'The framework user selected in QuickStart content.';
      exampleValues: [
        FrameworkName.PYTHON_PYTORCH,
        FrameworkName.KERAS_TENSOR_FLOW,
      ];
    };
    colabLink: {
      description: "The selected framework's corresponding Colab link when the button is clicked.";
      exampleValues: [
        FrameworkName.PYTHON_PYTORCH,
        FrameworkName.KERAS_TENSOR_FLOW,
      ];
    };
  } & QuickStartDefaultDescriptions
>(QuickStartEventNames.ClickedOpenInColab);

export const copiedApiKey = makeTrackEvent<
  {
    frameworkName: string;
  } & QuickStartDefaultData,
  {
    _description: 'Clicking on the copy button in the api key section of quick start.';
    _location: 'In the quick start content.';
    _motivation: 'Track data to determine how frequently users copy their api key in quick start.';
    frameworkName: {
      description: 'The framework user selected in QuickStart content.';
      exampleValues: [
        FrameworkName.PYTHON_PYTORCH,
        FrameworkName.KERAS_TENSOR_FLOW,
      ];
    };
  } & QuickStartDefaultDescriptions
>(QuickStartEventNames.CopiedApiKey);

export const copiedCodeBlock = makeTrackEvent<
  {
    frameworkName: string;
    codeSnippet: string;
  } & QuickStartDefaultData,
  {
    _description: 'A user clicked on the copy button in the code snippet in quick start.';
    _location: 'In the quick start content with multiple code snippet blocks.';
    _motivation: 'Track data to determine how frequently users copy a code snippet block in QuickStart.';
    frameworkName: {
      description: 'The framework user selected in QuickStart content.';
      exampleValues: [
        FrameworkName.PYTHON_PYTORCH,
        FrameworkName.KERAS_TENSOR_FLOW,
      ];
    };
    codeSnippet: {
      description: 'The code snippet user copied.';
      exampleValues: ['pip install wandb', 'wandb login'];
    };
  } & QuickStartDefaultDescriptions
>(QuickStartEventNames.CopiedCodeBlock);

export const clickedWeavePlayground = makeTrackEvent<
  QuickStartDefaultData,
  {
    _description: 'A user clicked on the Weave Playground button in quick start.';
    _location: 'In the quick start content.';
    _motivation: 'Track data to determine how frequently users click on the Weave Playground button in quick start.';
  } & QuickStartDefaultDescriptions
>(QuickStartEventNames.ClickedWeavePlayground);

// This event is used to track participation in the authorization flow from a colab notebook.
type ColabAuthFlowDefaultData = {
  apiKeyEmitted?: boolean;
};

export const colabAuthFlow = makeTrackEvent<
  ColabAuthFlowDefaultData,
  {
    apiKeyEmitted: {
      description: 'Whether an api key string was emitted to the colab notebook.';
      exampleValues: [true, false];
    };
    _description: 'An authorization flow to get an api key was triggered from a colab notebook.';
    _location: 'In the authorize page, visited from a colab notebook.';
    _motivation: 'Track data to determine how frequently this authorization flow is used.';
  }
>(QuickStartEventNames.AuthorizedFromColab);
